import { getBackendUrl } from '../utils/utils';

export class HttpError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'HttpError';
  }
}

export interface ContactInfo {
  contact_number: string | null;
  location: string | null;
  /** True while contact_number is the number the user last verified by SMS code. */
  phone_verified?: boolean;
}

export interface ContactInfoUpdate {
  contact_number?: string | null;
  location?: string | null;
  /** Proof id from /auth/phone/verify-code for contact_number; needed to change it while verification is on. */
  phone_verification_token?: string;
}

export interface SubscriptionInfo {
  is_trial: boolean;
  subscription_status: string | null;
  subscription_plan: string | null;
  access_end_date: string | null;
  seats_purchased: number | null;
  cancel_at_period_end: boolean;
  is_owner: boolean;
  is_expired: boolean;
}

export interface Transaction {
  id: string;
  plan_type: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
}

async function apiFetch<T>(url: string, token: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new HttpError(response.status, err.detail ?? `Request failed (${response.status})`);
  }
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as unknown as T;
  }
  return response.json();
}

export async function fetchContactInfo(token: string): Promise<ContactInfo> {
  return apiFetch<ContactInfo>(`${getBackendUrl()}/user/me/contact-info`, token);
}

/** Throws (409 with the backend's message) if another account already uses ``contactNumber``. */
export async function checkPhoneAvailable(token: string, contactNumber: string): Promise<void> {
  await apiFetch<{ available: boolean }>(`${getBackendUrl()}/user/me/contact-info/check-phone`, token, {
    method: 'POST',
    body: JSON.stringify({ contact_number: contactNumber }),
  });
}

export async function updateContactInfo(token: string, data: ContactInfoUpdate): Promise<ContactInfo> {
  return apiFetch<ContactInfo>(`${getBackendUrl()}/user/me/contact-info`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateUserFields(
  token: string,
  userId: string,
  fields: { display_name?: string; preferred_name?: string; role?: 'trainee' | 'trainer'; language?: string },
): Promise<void> {
  await apiFetch(`${getBackendUrl()}/user/${userId}`, token, {
    method: 'PUT',
    body: JSON.stringify(fields),
  });
}

export async function updatePassword(token: string, currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
  await apiFetch(`${getBackendUrl()}/user/me/password`, token, {
    method: 'PUT',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword }),
  });
}

export async function fetchSubscriptionInfo(token: string): Promise<SubscriptionInfo> {
  return apiFetch<SubscriptionInfo>(`${getBackendUrl()}/payment/subscription`, token);
}

export async function fetchTransactions(token: string): Promise<Transaction[]> {
  return apiFetch<Transaction[]>(`${getBackendUrl()}/payment/transactions`, token);
}

export async function cancelSubscription(token: string): Promise<{ cancelled: boolean; message: string }> {
  return apiFetch(`${getBackendUrl()}/payment/cancel-subscription`, token, { method: 'POST' });
}

export interface SeatsInfo {
  seats_purchased: number;
  seats_used: number;
  seats_remaining: number;
}

export interface InvitedUser {
  id: string;
  email: string | null;
  display_name: string | null;
  role: string | null;
  subscription_status: string | null;
  access_end_date: string | null;
}

export async function fetchSeatsInfo(token: string): Promise<SeatsInfo> {
  return apiFetch<SeatsInfo>(`${getBackendUrl()}/payment/seats-info`, token);
}

export async function fetchInvitedUsers(token: string): Promise<InvitedUser[]> {
  return apiFetch<InvitedUser[]>(`${getBackendUrl()}/payment/invited-users`, token);
}

export async function revokeInvitedUser(token: string, inviteeId: string): Promise<void> {
  await apiFetch(`${getBackendUrl()}/payment/revoke-invite/${inviteeId}`, token, { method: 'POST' });
}

export async function restoreInvitedUser(token: string, inviteeId: string): Promise<{ subscription_status: string }> {
  return apiFetch(`${getBackendUrl()}/payment/restore-invite/${inviteeId}`, token, { method: 'POST' });
}

export async function resendInvitedUser(token: string, inviteeId: string): Promise<void> {
  await apiFetch(`${getBackendUrl()}/payment/resend-invite/${inviteeId}`, token, { method: 'POST' });
}

export interface DowngradeSeatsResult {
  seats_purchased: number;
  message: string;
}

export async function downgradeSeats(token: string, newSeatCount: number): Promise<DowngradeSeatsResult> {
  return apiFetch<DowngradeSeatsResult>(`${getBackendUrl()}/payment/downgrade-seats`, token, {
    method: 'POST',
    body: JSON.stringify({ new_seat_count: newSeatCount }),
  });
}
